import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Menu, Heart, User, LogOut, ChevronDown, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { customer, isLoggedIn, logout } = useAuth();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch all products for search
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setAllProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, []);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = allProducts.filter(
      (product) =>
        product.name.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query)
    ).slice(0, 6);

    setSearchResults(results);
    setShowSearchResults(results.length > 0);
  }, [searchQuery, allProducts]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close search on route change
  useEffect(() => {
    setSearchQuery("");
    setShowSearchResults(false);
  }, [location]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/30" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <span className="font-heading text-xl md:text-2xl font-medium text-foreground tracking-wide">
              Artisan Bridge <span className="text-primary">Home</span>
            </span>
          </Link>

          {/* Desktop Navigation - Clean & Minimal */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <Link
              to="/"
              className={`nav-link font-body text-sm font-medium transition-colors ${
                isActive("/") && location.pathname === "/"
                  ? "text-primary"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`nav-link font-body text-sm font-medium transition-colors ${
                isActive("/about")
                  ? "text-primary"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              About Us
            </Link>
            
            {/* Categories Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className={`nav-link font-body text-sm font-medium flex items-center gap-1 transition-colors ${
                location.pathname.startsWith('/category')
                  ? "text-primary"
                  : "text-foreground/70 hover:text-foreground"
              }`}>
                Categories <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="min-w-[180px]">
                {categories.map((cat) => (
                  <DropdownMenuItem key={cat.id} asChild>
                    <Link 
                      to={`/category/${cat.id}`} 
                      className={`cursor-pointer ${
                        isActive(`/category/${cat.id}`) ? "text-primary" : ""
                      }`}
                    >
                      {cat.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Search Bar - Bronze style */}
            <div className="relative" ref={searchRef}>
              <div className="flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-2 hover:border-primary/50 transition-colors">
                <Search className="w-4 h-4 text-primary" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-0 bg-transparent h-6 w-28 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-primary/50 text-foreground text-sm"
                  data-testid="search-input"
                />
              </div>

              {/* Search Results Dropdown */}
              {showSearchResults && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50" data-testid="search-results">
                  {searchResults.map((product) => (
                    <Link
                      key={product.id}
                      to={`/product/${product.id}`}
                      className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                      onClick={() => setSearchQuery("")}
                    >
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground text-sm truncate">{product.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{product.category?.replace(/-/g, ' ')}</p>
                      </div>
                    </Link>
                  ))}
                  {searchResults.length === 0 && searchQuery.trim() !== "" && (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No products found
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>

          {/* Right side - Auth & Saved */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <Link to="/saved">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="relative"
                    data-testid="saved-products-btn"
                  >
                    <Heart className="w-5 h-5" />
                    {customer?.saved_products?.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                        {customer.saved_products.length}
                      </span>
                    )}
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="rounded-full gap-2"
                      data-testid="user-menu-btn"
                    >
                      <User className="w-4 h-4" />
                      <span className="max-w-[100px] truncate">{customer?.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/saved" className="flex items-center gap-2 cursor-pointer">
                        <Heart className="w-4 h-4" />
                        Saved Products
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="flex items-center gap-2 cursor-pointer text-destructive"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Link to="/login">
                <Button 
                  className="rounded-full bg-primary hover:bg-primary/90 px-6"
                  data-testid="login-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Login
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            {isLoggedIn && (
              <Link to="/saved">
                <Button variant="ghost" size="icon" className="relative">
                  <Heart className="w-5 h-5" />
                  {customer?.saved_products?.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center">
                      {customer.saved_products.length}
                    </span>
                  )}
                </Button>
              </Link>
            )}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-background">
                <div className="flex flex-col gap-6 mt-8">
                  {/* Mobile Search */}
                  <div className="relative">
                    <div className="flex items-center gap-2 bg-muted/50 rounded-full px-4 py-2 border border-border/50">
                      <Search className="w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search products..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border-0 bg-transparent h-8 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/60"
                        data-testid="mobile-search-input"
                      />
                    </div>

                    {/* Mobile Search Results */}
                    {showSearchResults && (
                      <div className="mt-2 bg-card border border-border rounded-xl overflow-hidden">
                        {searchResults.map((product) => (
                          <Link
                            key={product.id}
                            to={`/product/${product.id}`}
                            className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
                            onClick={() => {
                              setIsOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-foreground text-sm truncate">{product.name}</p>
                              <p className="text-xs text-muted-foreground capitalize">{product.category?.replace(/-/g, ' ')}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {isLoggedIn ? (
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{customer?.name}</p>
                        <p className="text-xs text-muted-foreground">{customer?.email}</p>
                      </div>
                    </div>
                  ) : (
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button className="w-full rounded-full bg-primary">
                        <User className="w-4 h-4 mr-2" />
                        Login / Register
                      </Button>
                    </Link>
                  )}
                  
                  <nav className="flex flex-col gap-2">
                    <Link
                      to="/"
                      onClick={() => setIsOpen(false)}
                      className={`font-body text-base font-medium transition-colors py-2 px-3 rounded-lg ${
                        location.pathname === "/"
                          ? "text-primary bg-primary/10"
                          : "text-foreground/70 hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      Home
                    </Link>
                    <Link
                      to="/about"
                      onClick={() => setIsOpen(false)}
                      className={`font-body text-base font-medium transition-colors py-2 px-3 rounded-lg ${
                        location.pathname === "/about"
                          ? "text-primary bg-primary/10"
                          : "text-foreground/70 hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      About Us
                    </Link>
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        onClick={() => setIsOpen(false)}
                        className={`font-body text-base font-medium transition-colors py-2 px-3 rounded-lg ${
                          isActive(`/category/${cat.id}`)
                            ? "text-primary bg-primary/10"
                            : "text-foreground/70 hover:text-foreground hover:bg-muted"
                        }`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </nav>

                  {isLoggedIn && (
                    <>
                      <div className="border-t border-border pt-4">
                        <Link
                          to="/saved"
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-3 py-2 px-3 rounded-lg text-foreground/70 hover:text-foreground hover:bg-muted"
                        >
                          <Heart className="w-5 h-5" />
                          Saved Products
                        </Link>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleLogout();
                          setIsOpen(false);
                        }}
                        className="rounded-full"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </Button>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
